import { useState, useRef, useEffect, useMemo, lazy, Suspense } from 'react'
import URLDetectionCard, { detectPastedURL } from './URLDetectionCard'
import ContentMenu from './ContentMenu'
import { useChatStore } from '../stores/chatStore'
import { useToastStore } from '../stores/toastStore'

const MultiCardScanner = lazy(() => import('./multicard/MultiCardScanner'))
const LiveCamera = lazy(() => import('./LiveCamera'))

function detectHashtags(text: string): string[] {
  const matches = text.match(/#[a-zA-Z][a-zA-Z0-9_]{1,49}/g)
  return matches ? [...new Set(matches.map((m) => m.slice(1)))] : []
}

interface Props {
  onSend: (message: string) => void
  disabled: boolean
  threadId: number
  onImport?: () => void
}

export default function ChatInput({ onSend, disabled, threadId, onImport }: Props) {
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const hashtags = useMemo(() => detectHashtags(message), [message])
  const { streaming, stopStreaming, fetchTimeline } = useChatStore()

  // URL detection state
  const [detectedURL, setDetectedURL] = useState<{ url: string; urlType: 'youtube' | 'blog' } | null>(null)
  const dismissedURLRef = useRef<string | null>(null)

  // Content menu state
  const [contentMenuOpen, setContentMenuOpen] = useState(false)

  // Multi-card scanner state
  const [multiCardOpen, setMultiCardOpen] = useState(false)
  const [initialScanFile, setInitialScanFile] = useState<File | null>(null)

  // Live camera state
  const [liveCameraOpen, setLiveCameraOpen] = useState(false)
  const cameraStreamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 150) + 'px'
    }
  }, [message])

  const handleSubmit = () => {
    const trimmed = message.trim()
    if (!trimmed || disabled) return
    // Catch URLs typed or pasted without triggering paste event
    if (!detectedURL && trimmed !== dismissedURLRef.current) {
      const detected = detectPastedURL(trimmed)
      if (detected) {
        setMessage(detected.url)
        setDetectedURL(detected)
        return
      }
    }
    dismissedURLRef.current = null
    setDetectedURL(null)
    onSend(trimmed)
    setMessage('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    if (e.key === 'Enter' && !e.shiftKey && !isTouchDevice) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text')
    const detected = detectPastedURL(pasted)
    if (detected && detected.url !== dismissedURLRef.current) {
      e.preventDefault()
      setMessage(detected.url)
      setDetectedURL(detected)
    }
  }

  const handleImportSuccess = () => {
    onImport?.()
    fetchTimeline(threadId)
    setMessage('')
    dismissedURLRef.current = null
  }

  const handleImageCapture = (file: File) => {
    onImport?.()
    setInitialScanFile(file)
    setMultiCardOpen(true)
  }

  const liveCameraGalleryRef = useRef<HTMLInputElement>(null)

  const handleLiveCamera = (stream: MediaStream) => {
    cameraStreamRef.current = stream
    setLiveCameraOpen(true)
  }

  const handleLiveCameraCapture = (file: File) => {
    cameraStreamRef.current = null
    setLiveCameraOpen(false)
    handleImageCapture(file)
  }

  const handleLiveCameraGallery = () => {
    cameraStreamRef.current = null
    setLiveCameraOpen(false)
    liveCameraGalleryRef.current?.click()
  }

  const handleLiveCameraClose = () => {
    cameraStreamRef.current = null
    setLiveCameraOpen(false)
  }

  const handleLiveCameraGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleImageCapture(file)
    e.target.value = ''
  }

  const handleScanComplete = (count: number) => {
    onImport?.()
    setMultiCardOpen(false)
    setInitialScanFile(null)
    fetchTimeline(threadId)
    useToastStore.getState().addToast(
      `${count} card${count > 1 ? 's' : ''} scanned`,
      'success',
    )
  }

  return (
    <>
      <div className="bg-surface border-t border-elevated shrink-0">
        {/* URL detection card */}
        {detectedURL && (
          <URLDetectionCard
            url={detectedURL.url}
            urlType={detectedURL.urlType}
            threadId={threadId}
            onDismiss={() => {
              dismissedURLRef.current = detectedURL.url
              setDetectedURL(null)
            }}
            onImportSuccess={handleImportSuccess}
          />
        )}

        {/* Hashtag pills */}
        {hashtags.length > 0 && (
          <div className="flex gap-1 px-4 pt-3 pb-0 overflow-x-auto">
            {hashtags.map((tag) => (
              <span key={tag} className="shrink-0 px-2 py-0.5 rounded text-xs bg-accent/15 text-accent">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Input row */}
        <div className="flex items-end gap-2 px-4 py-3">
          {/* Add content button */}
          <button
            onClick={() => setContentMenuOpen(true)}
            disabled={disabled}
            className="p-3 text-text-secondary hover:text-accent transition-colors disabled:opacity-30 shrink-0"
            aria-label="Add content"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={disabled ? 'AI is thinking...' : 'Ask a question...'}
            disabled={disabled}
            rows={1}
            data-ph-mask
            className="flex-1 bg-elevated text-text-primary placeholder-text-secondary border border-white/20 rounded-xl px-4 py-3 resize-none outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
          />

          {streaming ? (
            <button
              onClick={stopStreaming}
              className="p-3 bg-error/80 text-white rounded-xl hover:bg-error transition-colors shrink-0"
              aria-label="Stop generating"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={disabled || !message.trim()}
              aria-label="Send message"
              className="p-3 bg-accent text-bg rounded-xl hover:bg-accent-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content menu bottom sheet */}
      <ContentMenu
        open={contentMenuOpen}
        onClose={() => setContentMenuOpen(false)}
        threadId={threadId}
        onImageCapture={handleImageCapture}
        onLiveCamera={handleLiveCamera}
      />

      {/* Live camera (lazy loaded) */}
      {liveCameraOpen && cameraStreamRef.current && (
        <Suspense fallback={null}>
          <LiveCamera
            stream={cameraStreamRef.current}
            onCapture={handleLiveCameraCapture}
            onGallery={handleLiveCameraGallery}
            onClose={handleLiveCameraClose}
          />
        </Suspense>
      )}

      {/* Hidden gallery input for LiveCamera gallery fallback */}
      <input
        ref={liveCameraGalleryRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
        onChange={handleLiveCameraGalleryChange}
        className="hidden"
      />

      {/* Multi-card scanner (lazy loaded) */}
      {multiCardOpen && (
        <Suspense fallback={null}>
          <MultiCardScanner
            threadId={threadId}
            initialFile={initialScanFile ?? undefined}
            onClose={() => {
              setMultiCardOpen(false)
              setInitialScanFile(null)
            }}
            onComplete={handleScanComplete}
          />
        </Suspense>
      )}
    </>
  )
}
